import { zyAuthor } from '@/lib/servers';
import type { Reference as ReferenceType } from '@/lib/zybooks-api';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import { type Editor, Node, nodePasteRule } from '@tiptap/core';
import { type Node as PMNode } from '@tiptap/pm/model';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { v4 as uuidv4 } from 'uuid';
import { ReferenceNodeView } from './reference-node-view';
import { destroyReferenceStore, getOrCreateReferenceStore, getReferenceStorage } from './reference-store';

export type { ReferenceStorage } from './reference-store';

const REFERENCE_SEGMENT_REGEX = '([^#]+)';
const REFERENCE_TOKEN_REGEX = new RegExp(
    `reference:${REFERENCE_SEGMENT_REGEX}#(?:${REFERENCE_SEGMENT_REGEX}#)?(?:${REFERENCE_SEGMENT_REGEX}#)?`,
    'i',
);
const REFERENCE_TOKEN_CAPTURE_REGEX = new RegExp(
    `^reference:${REFERENCE_SEGMENT_REGEX}#(?:${REFERENCE_SEGMENT_REGEX}#)?(?:${REFERENCE_SEGMENT_REGEX}#)?$`,
    'i',
);

const collabReferenceSyncInitialized = new WeakSet<Editor>();
const seenCollabReferenceMessageIds = new WeakMap<Editor, Set<string>>();
const highlightClearPointerDownListeners = new WeakMap<Editor, (event: PointerEvent) => void>();

type ReferenceNodeAttrs = {
    sectionId: string;
    nodeId: string | null;
    subNodeId: string | null;
};

type PendingDeletedReference = ReferenceNodeAttrs & {
    referenceSectionGuid: string;
};

const pendingDeletedReferencesByEditor = new WeakMap<Editor, Map<string, PendingDeletedReference>>();
const pendingRecreateIdsByEditor = new WeakMap<Editor, Set<string>>();

type CollabReferenceSyncPayload = {
    messageId: string;
    references: ReferenceType[];
};

function parseReferenceToken(token: string): { sectionId: string | null; nodeId: string | null; subNodeId: string | null } {
    const captures = token.match(REFERENCE_TOKEN_CAPTURE_REGEX);

    return {
        sectionId: captures?.[1] ?? null,
        nodeId: captures?.[2] ?? null,
        subNodeId: captures?.[3] ?? null,
    };
}

function getCollaborationProvider(editor: Editor): TiptapCollabProvider | null {
    const storage = editor.storage as typeof editor.storage & {
        collaborationNodePresence?: {
            provider: TiptapCollabProvider | null;
        };
    };
    return storage.collaborationNodePresence?.provider ?? null;
}

function isCollabReferenceSyncPayload(value: unknown): value is CollabReferenceSyncPayload {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const payload = value as { messageId?: unknown; references?: unknown };
    return typeof payload.messageId === 'string' && Array.isArray(payload.references);
}

function broadcastCollabReferenceUpdate(editor: Editor, reference: ReferenceType): void {
    const awareness = getCollaborationProvider(editor)?.awareness;
    if (!awareness) {
        return;
    }

    const payload: CollabReferenceSyncPayload = {
        messageId: uuidv4(),
        references: [reference],
    };
    awareness.setLocalStateField('referenceSync', payload);
}

function initializeCollabReferenceSync(editor: Editor): void {
    if (collabReferenceSyncInitialized.has(editor)) {
        return;
    }

    const provider = getCollaborationProvider(editor);
    const awareness = provider?.awareness;
    if (!awareness) {
        return;
    }

    collabReferenceSyncInitialized.add(editor);

    const seenMessageIds = new Set<string>();
    seenCollabReferenceMessageIds.set(editor, seenMessageIds);
    const localClientId = awareness.clientID;

    const handleAwarenessUpdate = () => {
        if (editor.isDestroyed) {
            return;
        }

        for (const [clientId, state] of awareness.getStates()) {
            if (clientId === localClientId) {
                continue;
            }

            const syncPayload = (state as Record<string, unknown> | undefined)?.referenceSync;
            if (!isCollabReferenceSyncPayload(syncPayload)) {
                continue;
            }

            if (seenMessageIds.has(syncPayload.messageId)) {
                continue;
            }
            seenMessageIds.add(syncPayload.messageId);

            for (const reference of syncPayload.references) {
                const sourceSectionGuid = reference.source?.section_guid;
                const sourceNodeId = reference.source?.node_id;
                if (!sourceSectionGuid || !sourceNodeId) {
                    continue;
                }

                const alreadyStored = getReferenceStorage(editor).referencesByKey?.[sourceSectionGuid]?.[sourceNodeId];
                if (alreadyStored) {
                    continue;
                }

                storeReference(editor, reference);
            }
        }
    };

    awareness.on('update', handleAwarenessUpdate);
    editor.on('destroy', () => {
        awareness.off('update', handleAwarenessUpdate);
        collabReferenceSyncInitialized.delete(editor);
        seenCollabReferenceMessageIds.delete(editor);
    });
}

function clearHighlightedReferenceForTarget(editor: Editor, target: EventTarget | null): boolean {
    const highlightedId = getReferenceStorage(editor).highlightedReferenceNodeId;
    if (!highlightedId || !(target instanceof Element)) {
        return false;
    }

    const highlightedLink = target.closest(`a[data-reference-node-id="${highlightedId}"]`);
    if (highlightedLink) {
        return false;
    }

    getOrCreateReferenceStore(editor).setState({ highlightedReferenceNodeId: null });
    return true;
}

function removeHighlightClearOnPointerDown(editor: Editor): void {
    const listener = highlightClearPointerDownListeners.get(editor);
    if (!listener || typeof document === 'undefined') {
        return;
    }

    document.removeEventListener('pointerdown', listener, true);
    highlightClearPointerDownListeners.delete(editor);
}

function initializeHighlightClearOnPointerDown(editor: Editor): void {
    if (highlightClearPointerDownListeners.has(editor) || typeof document === 'undefined') {
        return;
    }

    const handlePointerDown = (event: PointerEvent) => {
        if (editor.isDestroyed) {
            return;
        }

        const didClearHighlight = clearHighlightedReferenceForTarget(editor, event.target);
        if (didClearHighlight) {
            removeHighlightClearOnPointerDown(editor);
        }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    highlightClearPointerDownListeners.set(editor, handlePointerDown);

    editor.on('destroy', () => {
        removeHighlightClearOnPointerDown(editor);
    });
}

function computeInboundReferenceUpdate(
    editor: Editor,
    current: ReturnType<typeof getReferenceStorage>,
    reference: ReferenceType,
): Record<string, { isReferenced: true; inboundReferences: ReferenceType[] }> | null {
    const currentSectionId = editor.state.doc.attrs?.sectionId as string | undefined;
    const destinationSectionGuid = reference.destination?.section_guid;
    const destinationNodeId = reference.destination?.sub_node_id || reference.destination?.node_id || null;

    if (!destinationSectionGuid || !destinationNodeId || !currentSectionId || destinationSectionGuid !== currentSectionId) {
        return null;
    }

    const existing = current.referencedNodesById[destinationNodeId];
    if (existing) {
        const alreadyPresent = existing.inboundReferences.some(
            inbound =>
                inbound.source?.section_guid === reference.source?.section_guid && inbound.source?.node_id === reference.source?.node_id,
        );
        if (alreadyPresent) {
            return null;
        }
        return {
            ...current.referencedNodesById,
            [destinationNodeId]: {
                ...existing,
                inboundReferences: [...existing.inboundReferences, reference],
            },
        };
    }

    return {
        ...current.referencedNodesById,
        [destinationNodeId]: { isReferenced: true, inboundReferences: [reference] },
    };
}

function storeReference(editor: Editor, reference: ReferenceType): void {
    const referenceSectionGuid = reference.source?.section_guid;
    const referenceNodeId = reference.source?.node_id;

    if (!referenceSectionGuid || !referenceNodeId) {
        return;
    }

    getOrCreateReferenceStore(editor).setState(current => {
        const docRefs = current.referencesByKey[referenceSectionGuid] ?? {};
        const newReferencesByKey = {
            ...current.referencesByKey,
            [referenceSectionGuid]: { ...docRefs, [referenceNodeId]: reference },
        };

        const newReferencedResourcesById = computeInboundReferenceUpdate(editor, current, reference);

        return newReferencedResourcesById
            ? { referencesByKey: newReferencesByKey, referencedNodesById: newReferencedResourcesById }
            : { referencesByKey: newReferencesByKey };
    });
}

function setReferencePending(editor: Editor, referenceSectionGuid: string, referenceNodeId: string, pending: boolean): void {
    getOrCreateReferenceStore(editor).setState(current => {
        const currentDocPending = current.pendingReferencesByKey[referenceSectionGuid] ?? {};

        if (pending) {
            return {
                pendingReferencesByKey: {
                    ...current.pendingReferencesByKey,
                    [referenceSectionGuid]: {
                        ...currentDocPending,
                        [referenceNodeId]: true,
                    },
                },
            };
        }

        if (!currentDocPending[referenceNodeId]) {
            return {};
        }

        const remainingDocPending = { ...currentDocPending };
        delete remainingDocPending[referenceNodeId];

        if (Object.keys(remainingDocPending).length === 0) {
            const remainingPendingByDoc = { ...current.pendingReferencesByKey };
            delete remainingPendingByDoc[referenceSectionGuid];
            return { pendingReferencesByKey: remainingPendingByDoc };
        }

        return {
            pendingReferencesByKey: {
                ...current.pendingReferencesByKey,
                [referenceSectionGuid]: remainingDocPending,
            },
        };
    });
}

function deleteReferenceFromServer(maDocumentId: number | null, referenceNodeId: string): void {
    void zyAuthor
        .delete('modern_authoring/reference', {
            body: {
                ma_document_id: maDocumentId,
                reference_node_id: referenceNodeId,
            },
        })
        .catch(error => {
            console.error('Failed to delete MA reference after node removal:', error);
        });
}

function removeReferenceFromStore(
    editor: Editor,
    referenceSectionGuid: string,
    referenceNodeId: string,
    fallbackDeletedAttrs?: ReferenceNodeAttrs,
): void {
    getOrCreateReferenceStore(editor).setState(current => {
        const existingByDoc = current.referencesByKey[referenceSectionGuid] ?? {};
        const referenceToRemove = existingByDoc[referenceNodeId] as ReferenceType | undefined;

        const nextByDoc = { ...existingByDoc };
        delete nextByDoc[referenceNodeId];

        const nextReferencesByKey = { ...current.referencesByKey };
        if (Object.keys(nextByDoc).length === 0) {
            delete nextReferencesByKey[referenceSectionGuid];
        } else {
            nextReferencesByKey[referenceSectionGuid] = nextByDoc;
        }

        const destinationSectionGuid =
            referenceToRemove?.destination?.section_guid ??
            (typeof fallbackDeletedAttrs?.sectionId === 'string' && fallbackDeletedAttrs.sectionId !== ''
                ? fallbackDeletedAttrs.sectionId
                : null);
        const destinationNodeId =
            referenceToRemove?.destination?.sub_node_id ||
            referenceToRemove?.destination?.node_id ||
            fallbackDeletedAttrs?.subNodeId ||
            fallbackDeletedAttrs?.nodeId ||
            null;

        if (!destinationSectionGuid || !destinationNodeId || destinationSectionGuid !== editor.state.doc.attrs?.sectionId) {
            return { referencesByKey: nextReferencesByKey };
        }

        const inboundMeta = current.referencedNodesById[destinationNodeId];
        if (!inboundMeta) {
            return { referencesByKey: nextReferencesByKey };
        }

        const nextInbound = inboundMeta.inboundReferences.filter(
            inbound => !(inbound.source?.section_guid === referenceSectionGuid && inbound.source?.node_id === referenceNodeId),
        );

        const nextReferencedNodesById = { ...current.referencedNodesById };
        if (nextInbound.length === 0) {
            delete nextReferencedNodesById[destinationNodeId];
        } else {
            nextReferencedNodesById[destinationNodeId] = {
                ...inboundMeta,
                inboundReferences: nextInbound,
            };
        }

        return {
            referencesByKey: nextReferencesByKey,
            referencedNodesById: nextReferencedNodesById,
        };
    });
}

function collectReferenceNodeAttrsById(doc: PMNode): Map<string, ReferenceNodeAttrs> {
    const referencesById = new Map<string, ReferenceNodeAttrs>();

    doc.descendants(node => {
        if (node.type.name !== 'reference') {
            return;
        }

        const referenceNodeId = node.attrs?.id;
        const sectionId = node.attrs?.sectionId;
        if (typeof referenceNodeId !== 'string' || referenceNodeId === '' || typeof sectionId !== 'string' || sectionId === '') {
            return;
        }

        const rawNodeId = node.attrs?.nodeId;
        const rawSubNodeId = node.attrs?.subNodeId;

        referencesById.set(referenceNodeId, {
            sectionId,
            nodeId: typeof rawNodeId === 'string' && rawNodeId !== '' ? rawNodeId : null,
            subNodeId: typeof rawSubNodeId === 'string' && rawSubNodeId !== '' ? rawSubNodeId : null,
        });
    });

    return referencesById;
}

function collectReferences(doc: PMNode): Set<string> {
    const nodeIds = new Set<string>();
    doc.descendants(node => {
        if (node.type.name === 'reference') {
            const referenceNodeId = node.attrs?.id;
            if (typeof referenceNodeId === 'string' && referenceNodeId !== '') {
                nodeIds.add(referenceNodeId);
            }
        }
    });
    return nodeIds;
}

function findDeletedReferences(oldDoc: PMNode, newDoc: PMNode): string[] {
    const oldRefs = collectReferences(oldDoc);
    const newRefs = collectReferences(newDoc);
    return [...oldRefs].filter(ref => !newRefs.has(ref));
}

function recreateReference(
    editor: Editor,
    maDocumentId: number | null,
    referenceNodeId: string,
    sectionId: string,
    nodeId: string | null,
    subNodeId: string | null,
    zybookId: number | null,
): void {
    const pendingRecreateIds = pendingRecreateIdsByEditor.get(editor);
    const pendingDeletedReferencesById = pendingDeletedReferencesByEditor.get(editor);
    if (!pendingRecreateIds || !pendingDeletedReferencesById) {
        return;
    }

    pendingRecreateIds.add(referenceNodeId);
    void zyAuthor
        .post('modern_authoring/reference', {
            body: {
                zybook_id: zybookId,
                ma_document_id: maDocumentId,
                reference_node_id: referenceNodeId,
                section_guid: sectionId,
                node_id: nodeId,
                sub_node_id: subNodeId,
            },
        })
        .then(() => {
            pendingRecreateIds.delete(referenceNodeId);
            pendingDeletedReferencesById.delete(referenceNodeId);
        })
        .catch(error => {
            pendingRecreateIds.delete(referenceNodeId);
            console.error('Failed to recreate MA reference after undo:', error);
        });
}

function handleReferenceDeletionTransaction(editor: Editor, oldDoc: PMNode, newDoc: PMNode): void {
    const pendingDeletedReferencesById = pendingDeletedReferencesByEditor.get(editor);
    const pendingRecreateIds = pendingRecreateIdsByEditor.get(editor);
    if (!pendingDeletedReferencesById || !pendingRecreateIds) {
        return;
    }

    const referenceSectionGuid = editor.state.doc.attrs?.sectionId;
    if (typeof referenceSectionGuid !== 'string' || referenceSectionGuid === '') {
        return;
    }

    const oldReferenceAttrsById = collectReferenceNodeAttrsById(oldDoc);
    const maDocumentId = (editor.state.doc.attrs?.maDocumentId as number | null | undefined) ?? null;
    for (const ref of findDeletedReferences(oldDoc, newDoc)) {
        const deletedRefAttrs = oldReferenceAttrsById.get(ref);
        if (deletedRefAttrs) {
            pendingDeletedReferencesById.set(ref, { ...deletedRefAttrs, referenceSectionGuid });
        }
        removeReferenceFromStore(editor, referenceSectionGuid, ref, deletedRefAttrs);
        deleteReferenceFromServer(maDocumentId, ref);
    }

    if (pendingDeletedReferencesById.size === 0) {
        return;
    }

    const newReferenceIds = collectReferences(newDoc);
    const zybookId = (editor.state.doc.attrs?.zybookId as number | null | undefined) ?? null;

    for (const [referenceNodeId, pendingRecord] of pendingDeletedReferencesById) {
        if (!newReferenceIds.has(referenceNodeId) || pendingRecreateIds.has(referenceNodeId)) {
            continue;
        }
        recreateReference(
            editor,
            maDocumentId,
            referenceNodeId,
            pendingRecord.sectionId,
            pendingRecord.nodeId,
            pendingRecord.subNodeId,
            zybookId,
        );
    }
}

function createReferenceFromPaste(
    editor: Editor,
    zybookId: number | null,
    maDocumentId: number | null,
    referenceSectionGuid: string,
    referenceNodeId: string,
    sectionId: string,
    nodeId: string | null,
    subNodeId: string | null,
) {
    setReferencePending(editor, referenceSectionGuid, referenceNodeId, true);

    void zyAuthor
        .post('modern_authoring/reference', {
            body: {
                zybook_id: zybookId,
                ma_document_id: maDocumentId,
                reference_node_id: referenceNodeId,
                section_guid: sectionId,
                node_id: nodeId,
                sub_node_id: subNodeId,
            },
        })
        .then(response => {
            if (!editor.isDestroyed) {
                const reference = response.reference as ReferenceType;
                storeReference(editor, reference);
                setReferencePending(editor, referenceSectionGuid, referenceNodeId, false);
                broadcastCollabReferenceUpdate(editor, reference);
            }
        })
        .catch(error => {
            if (!editor.isDestroyed) {
                setReferencePending(editor, referenceSectionGuid, referenceNodeId, false);
            }
            console.error('Failed to create MA reference from paste:', error);
        });
}

export const ReferenceHeadless = Node.create({
    name: 'reference',

    group: 'inline',

    inline: true,

    atom: true,

    selectable: true,

    draggable: false,

    onCreate() {
        getOrCreateReferenceStore(this.editor);
        pendingDeletedReferencesByEditor.set(this.editor, new Map());
        pendingRecreateIdsByEditor.set(this.editor, new Set());
        this.editor.on('destroy', () => {
            destroyReferenceStore(this.editor);
            pendingDeletedReferencesByEditor.delete(this.editor);
            pendingRecreateIdsByEditor.delete(this.editor);
        });
        initializeCollabReferenceSync(this.editor);
        initializeHighlightClearOnPointerDown(this.editor);
    },

    onTransaction({ editor, transaction }) {
        if (!transaction.docChanged) {
            return;
        }

        initializeCollabReferenceSync(editor);
        handleReferenceDeletionTransaction(editor, transaction.before, transaction.doc);
    },

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('id'),
                renderHTML: attributes => ({
                    id: attributes.id,
                }),
            },
            sectionId: {
                default: null,
                parseHTML: element => element.getAttribute('data-section-id') || element.getAttribute('data-document-id'),
                renderHTML: attributes => ({
                    'data-section-id': attributes.sectionId,
                }),
            },
            nodeId: {
                default: null,
                parseHTML: element => element.getAttribute('data-node-id'),
                renderHTML: attributes => ({
                    'data-node-id': attributes.nodeId,
                }),
            },
            subNodeId: {
                default: null,
                parseHTML: element => element.getAttribute('data-sub-node-id'),
                renderHTML: attributes => ({
                    'data-sub-node-id': attributes.subNodeId,
                }),
            },
            importTargetId: {
                default: null,
                parseHTML: element => element.getAttribute('data-import-target-id'),
                renderHTML: attributes => ({
                    'data-import-target-id': attributes.importTargetId,
                }),
            },
            sourceSectionId: {
                default: null,
                parseHTML: element => element.getAttribute('data-source-section-id'),
                renderHTML: attributes => ({
                    'data-source-section-id': attributes.sourceSectionId,
                }),
            },
            // Preview-only: never written to the collaborative doc (default null is omitted by Tiptap)
            displayText: {
                default: null,
                parseHTML: () => null,
                renderHTML: attributes => (attributes.displayText != null ? { 'data-display-text': attributes.displayText } : {}),
            },
            href: {
                default: null,
                parseHTML: () => null,
                renderHTML: attributes => (attributes.href != null ? { 'data-href': attributes.href } : {}),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="reference"]',
            },
            {
                tag: 'zyReference',
                getAttrs: element => {
                    // Parse the zyReference tag emitted during import
                    if (typeof element === 'string') return false;
                    const sourceId = element.getAttribute('id');
                    const targetId = element.getAttribute('target_id');
                    const sectionId = element.getAttribute('section_id');
                    return {
                        id: sourceId,
                        importTargetId: targetId,
                        sourceSectionId: sectionId,
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const displayText = HTMLAttributes['data-display-text'];
        const href = HTMLAttributes['data-href'];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { 'data-display-text': _dt, 'data-href': _href, ...restAttrs } = HTMLAttributes;
        const attrs = { ...restAttrs, 'data-type': 'reference' };

        if (displayText != null && href) {
            return ['a', { ...attrs, href, target: '_blank', rel: 'noopener noreferrer' }, displayText];
        }

        if (displayText != null) {
            return ['span', attrs, displayText];
        }

        return ['span', { ...HTMLAttributes, 'data-type': 'reference' }, 'REFERENCE'];
    },

    addPasteRules() {
        return [
            nodePasteRule({
                find: text => {
                    const match = text.match(REFERENCE_TOKEN_REGEX);
                    if (!match || match.index == null) {
                        return null;
                    }

                    return [{ index: match.index, text: match[0] }];
                },
                type: this.type,
                getAttributes: match => {
                    const matchedToken = typeof match?.[0] === 'string' ? match[0] : '';
                    const { sectionId, nodeId, subNodeId } = parseReferenceToken(matchedToken);
                    const referenceSectionGuid = this.editor?.state?.doc?.attrs?.sectionId;

                    // The reference does not yet exist while the paste handler is active, so
                    // a uuid needs to be generated here so it can be set correctly in the database.
                    const referenceNodeId = uuidv4();

                    const zybookId = this.editor.state.doc.attrs.zybookId;
                    const maDocumentId = this.editor.state.doc.attrs.maDocumentId;

                    if (sectionId && referenceSectionGuid && referenceNodeId) {
                        createReferenceFromPaste(
                            this.editor,
                            zybookId,
                            maDocumentId,
                            referenceSectionGuid,
                            referenceNodeId,
                            sectionId,
                            nodeId,
                            subNodeId,
                        );
                    }

                    return {
                        id: referenceNodeId,
                        sectionId,
                        nodeId,
                        subNodeId,
                    };
                },
            }),
        ];
    },
});

export const Reference = ReferenceHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ReferenceNodeView);
    },
});

export default Reference;
